// Copyright (c) StrangeBee 2023

import { useCallback, useState } from 'react';
import { useLocalMessage } from '@Helpers/locale';
import { FormatDate } from '@Molecules/Format';
import { ColumnType } from 'antd/lib/table';
import { THList } from '@Molecules/THList';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { ICONS } from '@Constants/iconsApp';
import { formatFileSize } from '@Helpers/stats';
import ExternalLink from '@Atoms/ExternalLink/ExternalLink';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { copyToClipboard } from '@Helpers/copy';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { AttachmentAlertType, AttachmentType, ObjectTypeV1 } from 'thehive-sdk';
import { AttachmentListProps } from '@Pages/Cases/Details/Attachments/List/types';
import { useDrawer } from '@Hooks/useDrawer';
import useConfigList from '@Hooks/useConfigList';
import client from '@Client';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import { computeEventList } from '@Organisms/Alert/eventList';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { AttachmentsCreate } from '@Organisms/Attachments/AttachmentsCreate/AttachmentsCreate';
import { THLayout } from '@Molecules/THLayout';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';

// AttachmentAlertType
export const AttachmentAlertsList = ({
    describe,
    parentId,
    previewManager,
    hasPermissions = false,
}: AttachmentListProps) => {
    const i = useLocalMessage();
    const { visible: visibleCreate, setVisible: setVisibleCreate } = useDrawer<AttachmentType>({});
    const [localFilterIsOpen, setLocalFilterIsOpen] = useState(false);

    const {
        dataSource,
        filterManager,
        storagePageManager,
        paginationManager,
        columnsRenderer,
        table,
        listRefetch,
        countRefetch,
        status = 'loading',
    } = useConfigList<AttachmentAlertType>({
        selector: {
            head: [{ name: 'getAlert', idOrName: parentId }, { name: 'attachments' }],
            name: 'get-alert-attachments',
        },
        controller: client.AlertController,
        storageId: StorageFilterEnum.ALERT_ATTACHMENTS,
        objectType: ObjectTypeV1.ALERT,
        streamRootId: parentId,
    });

    const eventList = computeEventList({
        controller: client.AlertController,
        locale: i,
        onError: handleNotificationError,
        history,
        onSuccess: (_, msg) => {
            openMessage('success', msg);
            listRefetch();
            countRefetch();
        },
    });

    const handleDelete = useCallback(
        (attachmentId: string) => {
            eventList.attachment
                .onDelete(parentId, attachmentId)
                .then(() => {
                    openMessage('success', i('attachment.action.success.delete'));
                    listRefetch();
                })
                .catch(handleNotificationError);
        },
        [parentId, eventList.attachment, i, listRefetch],
    );

    const getDownloadUrl = useCallback(
        (attachmentId: string): string =>
            window.location.origin + client.apiUrl + `/v1/alert/${parentId}/attachment/${attachmentId}/download`,
        [parentId],
    );

    const setLocalFilters = useCallback(
        (localFilters: boolean): void => {
            setLocalFilterIsOpen(localFilters);
            storagePageManager?.setFilterIsOpen?.(localFilters);
            filterManager.setEditFilters(localFilters);
        },
        [filterManager, storagePageManager],
    );

    const getColumns = useCallback(
        (): ColumnType<AttachmentAlertType>[] =>
            columnsRenderer([
                {
                    title: [
                        {
                            label: i('attachment.list.table.columns.filename'),
                            sortField: 'name',
                        },
                    ],
                    className: 'custom-table__cell',
                    render: (attachement) => {
                        const { name, contentType } = attachement;

                        return previewManager.allowPreview(contentType) ? (
                            <THButton
                                type="link"
                                onClick={() =>
                                    previewManager.togglePreview(attachement, { type: 'alert', id: parentId })
                                }
                            >
                                {name}
                            </THButton>
                        ) : (
                            <span>{name}</span>
                        );
                    },
                },
                {
                    title: [
                        {
                            label: i('attachment.list.table.columns.type'),
                            sortField: 'contentType',
                        },
                    ],
                    className: 'custom-table__cell',
                    render: ({ contentType }) => <span>{contentType}</span>,
                },
                {
                    title: [
                        {
                            label: i('attachment.list.table.columns.size'),
                            sortField: 'size',
                        },
                    ],
                    className: 'custom-table__cell',
                    render: ({ size }) => <span>{formatFileSize(size)}</span>,
                },
                {
                    title: [
                        {
                            label: i('attachment.list.table.columns.date'),
                            sortField: '_createdAt',
                        },
                    ],
                    className: 'custom-table__cell',
                    render: ({ _createdAt = 0 }) => <FormatDate date={_createdAt} />,
                },
                {
                    className: 'custom-table__cell',
                    render: ({ _id }) => (
                        <ActionsList
                            actionList={{
                                copy: {
                                    label: i('general.copy-url'),
                                    icon: <ICONS.CopyOutlined />,
                                    query: () => copyToClipboard(getDownloadUrl(_id), i('notification.copy')),
                                },
                                export: {
                                    label: i('general.download'),
                                    icon: <ICONS.DownloadOutlined />,
                                    CustomComponent: (triggerElem) => (
                                        <ExternalLink url={getDownloadUrl(_id)}>{triggerElem}</ExternalLink>
                                    ),
                                },
                                delete: {
                                    label: i('general.remove'),
                                    message: i('attachment.action.confirm.delete'),
                                    hidden: !hasPermissions,
                                    icon: <ICONS.DeleteIcon />,
                                    confirm: true,
                                    query: () => {
                                        handleDelete(_id);
                                    },
                                },
                            }}
                            scope="single"
                            isIcon
                        />
                    ),
                },
            ]),
        [columnsRenderer, getDownloadUrl, handleDelete, hasPermissions, i, parentId, previewManager],
    );

    return (
        <>
            <THLayout>
                <THList>
                    <THList.Header>
                        <THList.Slot name="MANAGE">
                            {hasPermissions && parentId && (
                                <AddButton label={i('attachment.sider.add')} onClick={() => setVisibleCreate(true)} />
                            )}
                        </THList.Slot>
                        <THList.Slot name="MANAGE">
                            <ManageSwitch
                                label={i('common.filters')}
                                value={localFilterIsOpen}
                                onChange={setLocalFilters}
                                icon={<ICONS.FilterOutlined />}
                            />
                        </THList.Slot>
                        <THList.Slot name="CONTENT">
                            {(localFilterIsOpen || !!filterManager.tempFilters.length) && (
                                <THFilter
                                    filterConfig={filterManager}
                                    describeField={describe?.[ObjectTypeV1.ATTACHMENT]}
                                >
                                    <Footer filterConfig={filterManager} />
                                </THFilter>
                            )}
                        </THList.Slot>
                    </THList.Header>

                    <THList.Content
                        dataSource={dataSource}
                        columns={getColumns()}
                        rowKey="_id"
                        emptyPlaceholder={<DisplayEmptyData spin={status === 'loading'} label={i('attachment.none')} />}
                    />

                    <THList.Footer>
                        <THList.Slot name="NAVIGATION">
                            <PaginationBrowse count={table.count} paginationConfig={paginationManager} />
                            <PaginationSize paginationConfig={paginationManager} />
                        </THList.Slot>
                    </THList.Footer>
                </THList>
            </THLayout>

            {visibleCreate && (
                <AttachmentsCreate
                    visible={visibleCreate}
                    onVisibleChange={setVisibleCreate}
                    onAddAttachment={(formData) => eventList.attachment.onAdd(parentId, formData)}
                />
            )}
        </>
    );
};
